/* CANFOTO ETAP 2 — R7 COLLECTION WHOLE-POOL READ MODEL
   Read-only Collection consumer over the sealed frozen canonical index.
   NO scoring, ranking, F/P, stock, DOM-order, commerce, filter, sort or URL ownership. */
(()=>{
'use strict';

const VERSION='CANFOTO-ETAP2-R7-COLLECTION-WHOLE-POOL-READ-MODEL-V1';
const EXPECTED_INDEX_VERSION='CANFOTO-ETAP2-R6-2H-E-FROZEN-CANONICAL-SNAPSHOT-V1';
const EXPECTED_PANEL_BATCH='8997a5ad451574377263c87fbbf79dfecc0671b79c04128a85e2f5ee8cdc6a1a';
const EXPECTED_COB_BATCH='1581f5b78ca4a979972ee6a1dc0e43ccaa20903afd3c7c37b431c739828780fa';

const clean=v=>String(v??'').replace(/\s+/g,' ').trim();
const freeze=v=>{
  if(!v||typeof v!=='object'||Object.isFrozen(v)) return v;
  Object.keys(v).forEach(k=>freeze(v[k]));
  return Object.freeze(v);
};
const sleep=ms=>new Promise(r=>setTimeout(r,ms));

const state={
  status:'BOOTING',
  version:VERSION,
  sourceMode:'FROZEN_CANONICAL_INDEX_READ_ONLY',
  indexVersion:'',
  poolCount:0,
  panelCount:0,
  cobCount:0,
  bareCount:0,
  singleReadySetCount:0,
  refreshCount:0,
  lastRefreshReason:'',
  lastRefreshAt:'',
  currentPage:null,
  error:'',
  scoringMutation:'NONE',
  rankingMutation:'NONE',
  fpMutation:'NONE',
  stockRankingMutation:'NONE',
  setupTruthMutation:'NONE',
  commerceMutation:'NONE',
  sortMutation:'NONE',
  filterMutation:'NONE',
  urlMutation:'NONE',
  domMutation:'NONE'
};

let byHandle=Object.freeze({});
let allRows=Object.freeze([]);

function currentSortValue(){
  const checked=document.querySelector('#SortByContainer input[name="sort_by"]:checked');
  if(checked) return clean(checked.value);
  const select=document.querySelector('#SortByContainer select[name="sort_by"]');
  return clean(select?.value);
}

function cardHandles(){
  const root=document.getElementById('ProductGridContainer')||document;
  const nodes=[...root.querySelectorAll(
    '[data-cf-ucard-rail="collection"] [data-cf-ucard-handle],'+
    '[data-cf-ucard-rail="collection"] [data-cf26-product-handle]'
  )];

  const out=[];
  const seen=new Set();
  for(const node of nodes){
    const h=clean(node.dataset.cfUcardHandle||node.dataset.cf26ProductHandle);
    if(!h||seen.has(h)) continue;
    seen.add(h);
    out.push(h);
  }
  return out;
}

function buildCurrentPage(reason='READ'){
  const handles=cardHandles();
  const canonical=[];
  const nonCanonical=[];
  const panel=[];
  const cob=[];

  for(const handle of handles){
    const row=byHandle[handle]||null;
    if(!row){
      nonCanonical.push(handle);
      continue;
    }
    canonical.push(handle);
    if(row.engineOwner==='GDX_PANEL_LED_V4') panel.push(handle);
    if(row.engineOwner==='GDX_COB_LED_V5') cob.push(handle);
  }

  return freeze({
    route:location.pathname+location.search,
    collectionPath:location.pathname,
    nativeSortValue:currentSortValue(),
    handles,
    productCardCount:handles.length,
    canonicalHandles:canonical,
    canonicalCount:canonical.length,
    nonCanonicalHandles:nonCanonical,
    nonCanonicalCount:nonCanonical.length,
    panelCanonicalHandles:panel,
    panelCanonicalCount:panel.length,
    cobCanonicalHandles:cob,
    cobCanonicalCount:cob.length,
    reason,
    capturedAt:new Date().toISOString()
  });
}

function refresh(reason='MANUAL'){
  state.currentPage=buildCurrentPage(reason);
  state.refreshCount+=1;
  state.lastRefreshReason=reason;
  state.lastRefreshAt=new Date().toISOString();

  window.dispatchEvent(new CustomEvent('canfoto:r7-collection-read-model-updated',{
    detail:{
      version:VERSION,
      refreshCount:state.refreshCount,
      reason,
      currentPage:state.currentPage
    }
  }));
  return state.currentPage;
}

function build(index){
  if(!index) throw new Error('R7_FROZEN_INDEX_MISSING');
  if(clean(index.version)!==EXPECTED_INDEX_VERSION)
    throw new Error('R7_FROZEN_INDEX_VERSION_MISMATCH:'+clean(index.version));

  const source=Object.values(index.products||{});
  const rows=[];
  const map={};

  for(const raw of source){
    const row=freeze({
      productHandle:clean(raw.productHandle),
      title:clean(raw.title),
      engineOwner:clean(raw.engineOwner),
      engine:clean(raw.engine),
      familyKey:clean(raw.familyKey),
      lane:clean(raw.lane),
      top5Count:Number(raw.top5Count||0),
      payloadHash:clean(raw.payloadHash),
      materializationVersion:clean(raw.materializationVersion),
      materializationBatchFingerprint:clean(raw.materializationBatchFingerprint),
      inputBatchFingerprint:clean(raw.inputBatchFingerprint),
      sharedV27AdapterVersion:clean(raw.sharedV27AdapterVersion)
    });

    if(!row.productHandle) throw new Error('R7_CANONICAL_HANDLE_MISSING');
    if(map[row.productHandle]) throw new Error('R7_DUPLICATE_CANONICAL_HANDLE:'+row.productHandle);

    map[row.productHandle]=row;
    rows.push(row);
  }

  const panel=rows.filter(x=>x.engineOwner==='GDX_PANEL_LED_V4');
  const cob=rows.filter(x=>x.engineOwner==='GDX_COB_LED_V5');
  const bare=rows.filter(x=>x.lane==='BARE_LIGHT');
  const ready=rows.filter(x=>x.lane==='SINGLE_READY_SET');

  if(rows.length!==Number(index.counts?.products||0))
    throw new Error('R7_POOL_COUNT_MISMATCH');
  if(panel.length!==Number(index.counts?.panelProducts||0))
    throw new Error('R7_PANEL_COUNT_MISMATCH');
  if(cob.length!==Number(index.counts?.cobProducts||0))
    throw new Error('R7_COB_COUNT_MISMATCH');
  if(panel.some(x=>x.top5Count!==5)||cob.some(x=>x.top5Count!==5))
    throw new Error('R7_TOP5_COUNT_CONTRACT_MISMATCH');
  if(panel.some(x=>x.materializationBatchFingerprint!==EXPECTED_PANEL_BATCH))
    throw new Error('R7_PANEL_BATCH_FINGERPRINT_MISMATCH');
  if(cob.some(x=>x.materializationBatchFingerprint!==EXPECTED_COB_BATCH))
    throw new Error('R7_COB_BATCH_FINGERPRINT_MISMATCH');

  byHandle=freeze(map);
  allRows=freeze(rows.slice());

  state.indexVersion=clean(index.version);
  state.poolCount=rows.length;
  state.panelCount=panel.length;
  state.cobCount=cob.length;
  state.bareCount=bare.length;
  state.singleReadySetCount=ready.length;
  state.status='READY';
  refresh('BOOT');

  return true;
}

async function boot(){
  try{
    let index=window.CANFOTO_R62HE_FROZEN_INDEX||null;
    if(!index){
      const started=performance.now();
      while(!index && performance.now()-started<15000){
        await sleep(50);
        index=window.CANFOTO_R62HE_FROZEN_INDEX||null;
      }
    }
    build(index);
    window.CANFOTO_R7_COLLECTION_READ_MODEL=API;
    window.dispatchEvent(new CustomEvent('canfoto:r7-collection-read-model-ready',{
      detail:{
        version:VERSION,
        sourceMode:state.sourceMode,
        counts:{
          products:state.poolCount,
          panelProducts:state.panelCount,
          cobProducts:state.cobCount,
          bareLight:state.bareCount,
          singleReadySet:state.singleReadySetCount
        }
      }
    }));
  }catch(error){
    state.status='ERROR_FAIL_CLOSED';
    state.error=clean(error?.message||error);
    window.CANFOTO_R7_COLLECTION_READ_MODEL=API;
    console.error('[CANFOTO R7 Collection Read Model]',error);
  }
}

const API=Object.freeze({
  version:VERSION,
  expectedIndexVersion:EXPECTED_INDEX_VERSION,
  sourceMode:'FROZEN_CANONICAL_INDEX_READ_ONLY',
  expectedPanelBatchFingerprint:EXPECTED_PANEL_BATCH,
  expectedCobBatchFingerprint:EXPECTED_COB_BATCH,

  getState(){
    return freeze({
      status:state.status,
      version:state.version,
      sourceMode:state.sourceMode,
      indexVersion:state.indexVersion,
      poolCount:state.poolCount,
      panelCount:state.panelCount,
      cobCount:state.cobCount,
      bareCount:state.bareCount,
      singleReadySetCount:state.singleReadySetCount,
      refreshCount:state.refreshCount,
      lastRefreshReason:state.lastRefreshReason,
      lastRefreshAt:state.lastRefreshAt,
      currentPage:state.currentPage,
      error:state.error,
      scoringMutation:state.scoringMutation,
      rankingMutation:state.rankingMutation,
      fpMutation:state.fpMutation,
      stockRankingMutation:state.stockRankingMutation,
      setupTruthMutation:state.setupTruthMutation,
      commerceMutation:state.commerceMutation,
      sortMutation:state.sortMutation,
      filterMutation:state.filterMutation,
      urlMutation:state.urlMutation,
      domMutation:state.domMutation
    });
  },

  getProduct(handle){
    return byHandle[clean(handle)]||null;
  },

  hasProduct(handle){
    return !!byHandle[clean(handle)];
  },

  getAll(){
    return allRows.slice();
  },

  getByEngine(engineOwner){
    const e=clean(engineOwner);
    return allRows.filter(x=>x.engineOwner===e);
  },

  getByLane(lane){
    const l=clean(lane);
    return allRows.filter(x=>x.lane===l);
  },

  getCurrentPage(){
    return state.currentPage||buildCurrentPage('READ');
  },

  refresh
});

document.addEventListener('collection:reloaded',()=>refresh('COLLECTION_RELOADED'));
window.addEventListener('popstate',()=>setTimeout(()=>refresh('POPSTATE'),0));

if(document.readyState==='loading'){
  document.addEventListener('DOMContentLoaded',boot,{once:true});
}else{
  boot();
}
})();